import React from 'react';
import { Box, Text, UnstyledButton } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { AttrRow, AttrValue, NO, ProductInfo, TooltipKey, YES } from '../compareData';
import { CheckIcon, CrossIcon } from '../CompareIcons';
import { GRAY, INK, MOBILE, MUTED } from '../constants';
import { ColumnBlock } from './ColumnBlock';

/** One attribute value: Yes/No checkmark or a spec line. */
const AttributeValue: React.FC<{ value: AttrValue }> = ({ value }) => {
  const { t } = useTranslation();
  if (value === YES || value === NO) {
    const yes = value === YES;
    return (
      <Box
        fw={500}
        c={yes ? INK : GRAY}
        sx={{ display: 'inline-flex', alignItems: 'center', gap: 10, fontSize: 16, [MOBILE]: { fontSize: 12.5 } }}
      >
        <Box
          bg={yes ? '#c9f0d5' : '#e5e5e2'}
          sx={{
            width: 26,
            height: 26,
            flex: 'none',
            borderRadius: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            '& svg': { width: 14, height: 14 },
            [MOBILE]: { width: 20, height: 20, '& svg': { width: 11, height: 11 } },
          }}
        >
          {yes ? <CheckIcon /> : <CrossIcon />}
        </Box>
        {yes ? t('compare_v2_yes') : t('compare_v2_no')}
      </Box>
    );
  }
  return (
    <Box fw={500} sx={{ fontSize: 16, [MOBILE]: { fontSize: 12.5 } }}>
      {t(value.valueKey)}
      {value.subKey && (
        <Text component="span" c={GRAY} fz={14} fw={400} mt={3} display="block">
          {t(value.subKey)}
        </Text>
      )}
    </Box>
  );
};

interface AttributeCellProps {
  row: AttrRow;
  product: ProductInfo;
  onOpenTooltip: (key: TooltipKey) => void;
}

/** An attribute cell: repeated label (+ optional info trigger) and its value. */
export const AttributeCell: React.FC<AttributeCellProps> = ({ row, product, onOpenTooltip }) => {
  const { t } = useTranslation();
  const value = product.attrs[row.attrKey];
  const label = t(row.labelKey);
  // Info icon only on columns that have the feature.
  const showInfo = Boolean(row.tooltip) && value === YES;

  let title: React.ReactNode = label;
  if (showInfo && row.tooltip) {
    const tooltip = row.tooltip;
    // Glue the icon to the last word so it never orphans.
    const words = label.split(' ');
    const last = words.pop();
    const lead = words.join(' ');
    title = (
      <>
        {lead ? `${lead} ` : null}
        <Text component="span" sx={{ whiteSpace: 'nowrap' }}>
          {last}
          <UnstyledButton
            type="button"
            w={17}
            h={17}
            p={0}
            ml={5}
            c={GRAY}
            aria-label={t('compare_v2_more_info')}
            onClick={() => onOpenTooltip(tooltip)}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              verticalAlign: 'middle',
              border: `1.5px solid ${MUTED}`,
              borderRadius: 9999,
              cursor: 'pointer',
              '&::after': { content: '"i"', font: 'italic 700 10px/1 "Times New Roman", serif' },
              '&:hover': { borderColor: INK, color: INK },
            }}
          />
        </Text>
      </>
    );
  }

  return (
    <ColumnBlock title={title}>
      <AttributeValue value={value} />
    </ColumnBlock>
  );
};
